import { createRoot } from 'react-dom/client';
import { createPortal } from 'react-dom';
import { Theme } from '@radix-ui/themes';

import { ThemeProvider } from 'styled-components';
import { QueryClientProvider } from '@tanstack/react-query';
import { Provider } from 'react-redux';

import store from '../lib/redux/store';
import { ActionCableContext, cable } from '../lib/actionCableContext';
import airboTheme from '../themes/airbo';
import ErrorBoundary from '../lib/ErrorBoundary.jsx';
import GlobalStyle from '../shared/GlobalStyle/index.jsx';
import NotificationList from '../shared/Notification';
import queryClient from '../lib/queryClient';
import { initSentry } from '../lib/sentry';
import Header from '../containers/Header';
import NavigationBar from '../containers/NavigationBar';

import BoardSetup from '../containers/boardSetup';
import Send from '../containers/send';
import Campaigns from '../containers/campaigns';
import Reports from '../containers/reports/index.jsx';
import ClientsTab from '../containers/clientAdminClients';
import ClientPrizesTab from '../containers/clientAdminPrizes';
import UsersTab from '../containers/clientAdminUsers/index.jsx';
import GoalsTab from '../containers/clientAdminGoals/GoalsContainer.jsx';
import AccountSettings from '../containers/accountSettings';
import HrAssistant from '../containers/hrAssistant/index.jsx';
import BroadcastMessages from '../containers/broadcastMessages/index.jsx';
import CampaignPreview from '../containers/campaignPreview';
import EmbedContainer from '../containers/embed/index.jsx';
import VaultTab from '../containers/vault';
import Plan from '../containers/plan';
import ReactSPA from '../app/index.jsx';
import { ChangelogButton } from '../shared/ChangelogButton';

// Initialized before render so uncaught errors during startup are reported too.
initSentry();

const container = document.getElementById('react-root');
const root = createRoot(container);

const registry = [
  {
    id: 'NavigationBar',
    component: NavigationBar,
    propsFromNode: node => ({ content: JSON.parse(node.dataset.content) }),
  },
  {
    id: 'Header',
    component: Header,
    propsFromNode: node => ({
      logo: node.dataset.logo,
      shouldConvertGuest: node.dataset.shouldConvertGuest,
      hideSignIn: node.dataset.hideSignIn,
      guestUserResetPath: node.dataset.guestUserResetPath,
      home: node.dataset.home,
    }),
  },
  { id: 'Reports', component: Reports },
  {
    id: 'ClientsTab',
    component: ClientsTab,
    propsFromBody: {
      organizationData: JSON.parse(document.body.dataset.currentOrganization),
    },
  },
  { id: 'ClientsPrizeTab', component: ClientPrizesTab },
  { id: 'UsersTab', component: UsersTab },
  { id: 'GoalsTab', component: GoalsTab },
  {
    id: 'Campaigns',
    component: Campaigns,
  },
  {
    id: 'BoardSetup',
    component: BoardSetup,
  },
  {
    id: 'Send',
    component: Send,
    propsFromNode: node => ({
      emailPreviewPath: node.dataset.titleDigestEmailPreviewPath,
      smsPreviewPath: node.dataset.titleDigestSmsPreviewPath,
    }),
  },
  {
    id: 'AccountSettings',
    component: AccountSettings,
  },
  {
    id: 'ReactSPA',
    component: ReactSPA,
    propsFromNode: node => ({ data: JSON.parse(node.dataset.ctrl) }),
  },
  {
    id: 'HrAssistant',
    component: HrAssistant,
    propsFromBody: {
      assistantUuid: document.body.dataset.assistantId,
      showSunsetBanner:
        document.body.dataset.showHrAssistantSunsetBanner === 'true',
    },
  },
  {
    id: 'BroadcastMessage',
    component: BroadcastMessages,
  },
  {
    id: 'CampaignPreview',
    component: CampaignPreview,
  },
  {
    id: 'EmbedContainer',
    component: EmbedContainer,
  },
  {
    id: 'VaultTab',
    component: VaultTab,
    propsFromBody: {
      organizationData: JSON.parse(document.body.dataset.currentOrganization),
    },
  },
  {
    id: 'Plan',
    component: Plan,
  },
  {
    id: 'ChangelogButton',
    component: ChangelogButton,
    propsFromNode: node => ({ page: node.dataset.page }),
  },
];

const empty = () => ({});

const createReactComponents = () =>
  registry
    .map(item => ({
      ...item,
      node: document.getElementById(item.id),
    }))
    .filter(item => Boolean(item.node))
    .map(
      ({
        component: Component,
        node,
        propsFromNode = empty,
        propsFromBody = {},
      }) =>
        createPortal(
          <Theme>
            <Component {...propsFromNode(node)} {...propsFromBody} />
          </Theme>,
          node,
        ),
    );

document.addEventListener('DOMContentLoaded', () => {
  root.render(
    <Theme appearance="light" accentColor="blue" panelBackground="solid">
      <ThemeProvider theme={airboTheme}>
        <QueryClientProvider client={queryClient}>
          <ErrorBoundary>
            <Provider store={store}>
              <ActionCableContext.Provider value={cable}>
                <GlobalStyle />
                <NotificationList />
                {createReactComponents()}
              </ActionCableContext.Provider>
            </Provider>
          </ErrorBoundary>
        </QueryClientProvider>
      </ThemeProvider>
    </Theme>,
  );
});
